import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { Modal } from '../components/Modal';
import { DeliveryForm, type DeliveryInfo } from '../components/DeliveryForm';
import { doc, updateDoc, collection, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useWallet } from '../context/WalletContext';
import { useAuth } from '../context/AuthContext';

interface Reward {
  id: string;
  title: string;
  imageUrl: string;
  price: number;
  isActive: boolean;
}

export function Notifications() {
  const navigate = useNavigate();
  const { notifications, markAsRead } = useNotifications();
  const { updateBalance } = useWallet();
  const { user } = useAuth();
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [availableRewards, setAvailableRewards] = useState<Reward[]>([]);

  useEffect(() => {
    const fetchRewards = async () => {
      const rewardsRef = collection(db, 'rewards');
      const snapshot = await getDocs(rewardsRef);
      const rewards = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Reward))
        .filter(reward => reward.isActive);
      setAvailableRewards(rewards);
    };

    fetchRewards();
  }, []);

  const handleNotificationClick = async (notification: any) => {
    console.log('Notification cliquée:', notification);
    
    // Vérifier seulement le status pending
    if (notification.status === 'pending') {
      setSelectedNotification(notification);
      setShowRewardModal(true);
    } else {
      console.log('Notification non éligible:', { 
        status: notification.status,
        type: notification.type
      });
    }
  };

  const handleDirectClaim = async () => {
    if (!selectedNotification) return;

    try {
      // Mettre à jour le solde de l'utilisateur
      await updateBalance(selectedNotification.amount);

      // Marquer la notification comme traitée
      await updateDoc(doc(db, 'notifications', selectedNotification.id), {
        claimed: true,
        claimType: 'direct',
        claimDate: new Date()
      });

      // Marquer la notification comme lue seulement après une réclamation réussie
      await markAsRead(selectedNotification.id);

      setShowRewardModal(false);
      setSelectedNotification(null);
    } catch (error) {
      console.error('Erreur lors de la réclamation directe:', error);
    }
  };

  const handleRewardSelect = (reward: Reward) => {
    setSelectedReward(reward);
    setShowRewardModal(false);
    setShowDeliveryForm(true);
  };

  const handleDeliverySubmit = async (deliveryInfo: DeliveryInfo) => {
    if (!selectedNotification || !user?.id) return;

    try {
      const deliveryRef = collection(db, 'deliveries');
      await addDoc(deliveryRef, {
        userId: user.id,
        notificationId: selectedNotification.id,
        rewardId: selectedReward?.id || '',
        rewardTitle: selectedReward?.title || '',
        rewardPrice: selectedReward?.price || 0,
        deliveryInfo,
        status: 'pending',
        createdAt: new Date(),
        drawDate: selectedNotification.date ? new Date(selectedNotification.date) : new Date()
      });

      // Mettre à jour le statut de la notification
      await markAsRead(selectedNotification.id);
      setShowDeliveryForm(false);
    } catch (error) {
      console.error('Erreur lors de la soumission de la livraison:', error);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="bg-black/100 text-white p-4 flex items-center justify-between sticky top-0 z-10 backdrop-blur-lg">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Notifications</h1>
        <div className="w-6" />
      </div>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {notifications.length === 0 ? (
          <p className="text-center text-gray-400">Aucune notification</p>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 rounded-lg cursor-pointer transition-colors ${
                  notification.status === 'pending'
                    ? 'border border-green-500/30 hover:border-green-500/50'
                    : 'border border-white/10 hover:border-white/20'
                }`}
              >
                <p className="text-white font-medium">
                  Félicitations ! Vous avez gagné {notification.amount} FCFA
                </p>
                <p className="text-gray-400 text-sm">
                  {notification.date instanceof Date 
                    ? notification.date.toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Date non disponible'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de choix de récompense */}
      <Modal
        isOpen={showRewardModal}
        onClose={() => setShowRewardModal(false)}
      >
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-white text-center whitespace-nowrap mb-4">Choisissez votre récompense</h2>
          
          <button
            onClick={handleDirectClaim}
            className="w-full bg-green-500 hover:bg-green-600 text-white p-4 rounded-xl transition-colors font-medium"
          >
            Direct {selectedNotification?.amount.toLocaleString()} FCFA
          </button>

          <div className="text-center text-gray-400 my-6 relative">
            <div className="absolute inset-y-1/2 left-0 right-0 h-px bg-white/10"></div>
            <span className="relative bg-black px-4">ou</span>
          </div>

          <div className="space-y-4">
            {availableRewards.map((reward) => (
              <button
                key={reward.id}
                onClick={() => handleRewardSelect(reward)}
                className="w-full bg-white/5 hover:bg-white/10 p-4 rounded-xl transition-colors text-left border border-green-500/30 hover:border-green-500/50"
              >
                <h3 className="text-white font-bold text-lg">{reward.title}</h3>
                <p className="text-sm text-gray-400">Valeur : {reward.price.toLocaleString()} FCFA</p>
                {reward.imageUrl && (
                  <div className="mt-4 h-64 relative overflow-hidden rounded-lg border border-white/5">
                    <img
                      src={reward.imageUrl}
                      alt={reward.title}
                      className="w-full h-full object-contain bg-black"
                    />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </Modal>

      {/* Formulaire de livraison */}
      <DeliveryForm
        isOpen={showDeliveryForm}
        onClose={() => setShowDeliveryForm(false)}
        onSubmit={handleDeliverySubmit}
        rewardTitle={selectedReward?.title || ''}
      />
    </div>
  );
} 